<template>
  <div
    v-if="showArtworkInfo"
    class="artwork-info"
    :class="{
      'artwork-info--large-bottom-margin': largeBottomMargin && !(isMultiUpload && showRedrawUI),
      'artwork-info--standard-artwork': isStandardArtwork,
      'artwork-info--no-margin': artwork.isLoading
    }"
  >
    <template v-if="metadata && !artwork.isLoading">
      <div
        class="artwork-info__file-info-row"
        :class="{ 'artwork-info__file-info-row--with-error': withError }"
      >
        <template v-if="showFileInfo">
          <UseSvg
            :icon="svgIcon"
            class="artwork-info__file-info-icon"
            :class="{ 'artwork-info__file-info-icon--large': showSteps }"
          />
          <div v-if="showSteps">
            {{ artwork.name }}
            <br />
            <b>{{ trimmedText }} {{ dimensionText }}, {{ pixelDimensionText }}</b>
          </div>
          <div v-else>
            <b>{{ trimmedText }} {{ dimensionText }}</b>
            <br />
            {{ pixelDimensionText }}
          </div>
        </template>
      </div>
      <button
        v-if="showRemoveButton"
        type="button"
        class="artwork-info__remove-button"
        @click="onRemove"
      >
        <UseSvg icon="trash" class="artwork-info__trash-icon" />
        Remove
      </button>
    </template>
  </div>
</template>

<script>
import { mapActions, mapState } from "pinia";
import UseSvg from "../vue-cart/UseSvg.vue";
import { useDtfStore } from "./store";
import { useSpanglesStore } from "./spangles-module";

export default {
  name: "ArtworkInfo",
  components: { UseSvg },
  props: {
    artwork: {
      type: Object,
      default: null
    },
    index: {
      type: Number,
      default: 0
    },
    showFileInfo: {
      type: Boolean,
      default: true
    },
    isMultiUpload: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapState(useDtfStore, [
      "showModal",
      "sizeErrorTypes",
      "isSpangle",
      "showStepsUI",
      "showRedrawUI",
      "isStandardArtwork"
    ]),
    ...mapState(useSpanglesStore, ["allAPIsCompleted"]),
    metadata() {
      const { removeBackground, metadata, withBgMetadata } = this.artwork || {};

      // if the image already comes with transparency
      if (!withBgMetadata || Object.keys(withBgMetadata).length === 0) {
        return metadata;
      }

      // otherwise, check for the flag for the switch, metadata will be image with background
      return removeBackground ? metadata : withBgMetadata;
    },
    withError() {
      return (
        this.metadata?.errors?.length > 0 ||
        this.artwork.errorMessage ||
        this.artwork.uploadError ||
        this.artwork.retryFailed
      );
    },
    showRemoveButton() {
      const hasSizeError = this.sizeErrorTypes.includes(this.artwork.errorType?.toString());
      const shouldShowButton = hasSizeError || this.showModal || !this.withError;

      if (this.isStandardArtwork) {
        return shouldShowButton && this.artwork.enhancementCompleted;
      }

      if (this.isSpangle) {
        return shouldShowButton && this.allAPIsCompleted;
      }

      return shouldShowButton;
    },
    svgIcon() {
      const icon = this.showSteps ? "empty-image-circle" : "check-bold";
      return this.withError ? "cross" : icon;
    },
    showArtworkInfo() {
      return (
        this.showModal ||
        ((this.metadata || this.artwork.isLoading) &&
          !this.artwork.uploadError &&
          !this.artwork.retryFailed)
      );
    },
    largeBottomMargin() {
      return this.showSteps && !this.artwork.isLoading;
    },
    trimmedText() {
      return this.metadata.trimmed ? "Trimmed size" : "Original size";
    },
    enhancedWidthInch() {
      if (this.isStandardArtwork) {
        if (!(this.artwork.enhanceImage && this.artwork.enhancementTier !== "custom")) {
          return this.artwork?.originalMetadata?.widthInch || this.metadata.widthInch;
        }

        return this.artwork.removeBackground
          ? this.artwork.enhancedMetadata?.widthInch || this.metadata.widthInch
          : this.artwork.withBgMetadata?.widthInch || this.metadata.widthInch;
      }

      return this.metadata.widthInch;
    },
    enhancedHeightInch() {
      if (this.isStandardArtwork) {
        if (!(this.artwork.enhanceImage && this.artwork.enhancementTier !== "custom")) {
          return this.artwork?.originalMetadata?.heightInch || this.metadata.heightInch;
        }

        return this.artwork.removeBackground
          ? this.artwork.enhancedMetadata?.heightInch || this.metadata.heightInch
          : this.artwork.withBgMetadata?.heightInch || this.metadata.heightInch;
      }

      return this.metadata.heightInch;
    },
    enhancedWidthPixel() {
      if (this.isStandardArtwork) {
        if (!(this.artwork.enhanceImage && this.artwork.enhancementTier !== "custom")) {
          return this.artwork?.originalMetadata?.width || this.metadata.width;
        }

        return this.artwork.removeBackground
          ? this.artwork.enhancedMetadata?.width || this.metadata.width
          : this.artwork.withBgMetadata?.width || this.metadata.width;
      }

      return this.metadata.width;
    },
    enhancedHeightPixel() {
      if (this.isStandardArtwork) {
        if (!(this.artwork.enhanceImage && this.artwork.enhancementTier !== "custom")) {
          return this.artwork?.originalMetadata?.height || this.metadata.height;
        }

        return this.artwork.removeBackground
          ? this.artwork.enhancedMetadata?.height || this.metadata.height
          : this.artwork.withBgMetadata?.height || this.metadata.height;
      }

      return this.metadata.height;
    },
    dimensionText() {
      return `${this.enhancedWidthInch}" x ${this.enhancedHeightInch}"`;
    },
    pixelDimensionText() {
      return `(${this.enhancedWidthPixel}px x ${this.enhancedHeightPixel}px)`;
    },
    showSteps() {
      return this.showStepsUI && !this.isMultiUpload;
    }
  },
  methods: {
    ...mapActions(useDtfStore, ["setRemovalFile"]),
    onRemove() {
      this.setRemovalFile(this.artwork.id);
    }
  }
};
</script>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

.artwork-info {
  position: relative;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &--standard-artwork {
    margin-bottom: 25px;
  }

  &--large-bottom-margin {
    margin-bottom: 30px;
  }

  &--no-margin {
    margin-bottom: 0;
  }

  &__file-info-row {
    font-size: 12px;
    font-weight: 400;
    line-height: 100%;
    display: flex;
    align-items: center;
    line-height: 18px;

    @include with-large-desktop {
      font-size: 14px;
    }

    &--with-error {
      color: $red;
    }
  }

  &__file-info-icon {
    width: 12px;
    height: 9px;
    margin-right: 6px;

    &--large {
      width: 24px;
      height: 24px;
      color: $crafterpay-gray-modern-400;
    }
  }

  &__remove-button {
    font-size: 13px;
    font-weight: 700;
    line-height: 20px;
    color: $primary-light;
    background: none;
    border: none;
    display: flex;
    align-items: center;
    height: 32px;
    right: 6px;
    top: -12px;

    @include with-large-desktop {
      top: -8px;
    }
  }

  &__trash-icon {
    width: 18px;
    height: 18px;
    margin-right: 3px;
  }
}
</style>
